import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import {
  FaUserTie,
  FaFingerprint,
  FaClock,
  FaChartPie,
} from 'react-icons/fa';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

const MiscCards = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();

  const cardStats = [
    { label: 'Total FTE', key: 'TotalFTECount', icon: <FaUserTie />, bg: 'from-[#1A2B5C] to-[#2E3C70]', format: 'count' },
    { label: 'Biometric Hrs', key: 'TotalBioMetricsHours', icon: <FaFingerprint />, bg: 'from-[#EE1D23] to-[#FB7185]', format: 'miscRoundedHrs' },
    { label: 'MISC Hrs', key: 'TotalMiscHours', icon: <FaClock />, bg: 'from-[#1A2B5C] to-[#4B5563]', format: 'miscRoundedHrs' },
    { label: 'MISC %', key: 'MiscPercentage', icon: <FaChartPie />, bg: 'from-[#EE1D23] to-[#FACC15]', format: 'string' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.entries(filters || {}).forEach(([key, value]) => {
          if (value) params[key] = value;
        });

        const res = await api.get(`${baseUrl}/superadminDashboard/getSuperAdminMiscReports`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });

        if (res.data.success) {
          setData(res.data.data);
        } else {
          setData(null);
        }
      } catch (err) {
        console.error('API Error:', err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, filters]);

  const CountUp = ({ end }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      if (typeof end !== 'number') return;
      let current = 0;
      const step = Math.ceil(end / 40);
      const interval = setInterval(() => {
        current += step;
        if (current >= end) {
          setCount(end);
          clearInterval(interval);
        } else {
          setCount(current);
        }
      }, 25);
      return () => clearInterval(interval);
    }, [end]);
    return <>{count}</>;
  };

  const formatValue = (value, formatType) => {
    if (loading) return '...';

    if (formatType === 'count') {
      return typeof value === 'number' ? <CountUp end={value} /> : 'N/A';
    }

    if (formatType === 'miscRoundedHrs') {
      if (typeof value === 'string') {
        const hrMatch = value.match(/(\d+)\s*hrs?/i);
        const minMatch = value.match(/(\d+)\s*min/i);
        const hrs = hrMatch ? parseInt(hrMatch[1]) : 0;
        const mins = minMatch ? parseInt(minMatch[1]) : 0;
        const total = hrs + mins / 60;
        return `${total.toFixed(1)} hrs`;
      }
      return value ?? 'N/A';
    }

    return value ?? 'N/A';
  };

  return (
    <div className="mt-10">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -500px 0; }
          100% { background-position: 500px 0; }
        }
        .shimmer {
          background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
          background-size: 1000px 100%;
          animation: shimmer 1.5s infinite linear;
        }
      `}</style>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {loading
          ? cardStats.map((_, i) => (
              <div key={i} className="p-6 bg-white border border-gray-200 shadow rounded-[2rem]">
                <div className="w-10 h-10 rounded-full mx-auto mb-3 shimmer" />
                <div className="h-4 w-24 mx-auto shimmer rounded mb-2" />
                <div className="h-4 w-20 mx-auto shimmer rounded" />
              </div>
            ))
          : data
          ? cardStats.map((stat, i) => {
              const value = data[stat.key];
              return (
                <div
                  key={i}
                  className="relative overflow-hidden p-6 rounded-[2rem] bg-white border border-gray-200 shadow-xl transition-transform group hover:-translate-y-1 hover:scale-[1.02] duration-500"
                >
                  <div className="absolute top-0 left-0 w-0 h-0 border-t-[60px] border-t-[#EE1D23] border-r-[60px] border-r-transparent rounded-tr-[1rem]" />
                  <div className="absolute bottom-0 right-0 w-0 h-0 border-b-[60px] border-b-[#1A2B5C] border-l-[60px] border-l-transparent rounded-bl-[1rem]" />
                  <div className={`absolute inset-0  bg-gradient-to-br ${stat.bg} opacity-10 rounded-[2rem]`} />
                  <div
                    className="absolute -inset-2  blur-2xl opacity-20 rounded-[2rem] group-hover:opacity-40 transition-all duration-700"
                    style={{ background: `radial-gradient(circle at top left, #EE1D2360, #1A2B5C40)` }}
                  />
                  <div className="absolute -top-16 -left-16 w-52 h-52 bg-[#EE1D23]/20 rounded-full blur-[100px] " />
                  <div className="absolute -bottom-16 -right-16 w-52 h-52 bg-[#1A2B5C]/20 rounded-full blur-[100px] " />

                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-white text-[#1A2B5C] text-xl rounded-full shadow-md group-hover:rotate-[6deg] transition-transform duration-300  relative">
                    {stat.icon}
                  </div>

                  <h4 className="text-center text-sm font-semibold uppercase text-[#1A2B5C] tracking-wide mb-1 relative">
                    {stat.label}
                  </h4>

                  <div className="text-center text-2xl font-bold text-[#1A2B5C] tracking-tight relative">
                    {formatValue(value, stat.format)}
                  </div>

                  <div className={`mt-3 h-[2px] w-12 mx-auto bg-gradient-to-r ${stat.bg} rounded-full  relative`} />
                </div>
              );
            })
          : (
            <div className="col-span-full text-center text-gray-500 font-semibold py-6">
              No data available
            </div>
          )}
      </div>
    </div>
  );
};

export default MiscCards;
